import { IOC } from '@/ioc/index';
import { defaultLocale } from '@/src/shared/utils/i18n';
import { serialize } from '@/src/shared/utils/serialization';

import { cmsContentPresenter } from '../presenters/cms-content-presenter';
import { type CMSContentViewModel } from '../types/cms-content.viewModel';

import type { CMSContentType } from '../../domain/types/cms-content.types';
import type { CacheHeadersBuilder } from '@/src/shared/domain/models/cache-headers-builder';
import type {
  CreateSsrDataOptions,
  SsrDataFactory
} from '@/src/shared/presentation/ssr/ssr-data-factory';
import type { AppLocale } from '@/src/shared/utils/i18n';
import type { JsonObject } from '@/src/shared/utils/serialization.types';

export class CmsContentForPathSSRFactory implements SsrDataFactory<CMSContentViewModel | null> {
  key = CmsContentForPathSSRFactory.getKey();

  constructor(
    private readonly path: string = '',
    private readonly language: AppLocale = defaultLocale,
    private readonly cacheHeadersBuilder: CacheHeadersBuilder | null = null
  ) {}

  async create({ logAndSaveError }: CreateSsrDataOptions) {
    try {
      const content = await IOC.gather('GetCMSContentForPath_UseCase').then((useCase) =>
        useCase.execute(this.path, this.language, this.cacheHeadersBuilder)
      );

      return cmsContentPresenter(content) || null;
    } catch (error) {
      logAndSaveError(this.key, error);
      return null;
    }
  }

  serialize = (data: CMSContentViewModel | null) => (data ? serialize(data) : null);

  deserialize(serializedData: JsonObject) {
    if (serializedData && '$type' in serializedData) {
      const typedData = serializedData as JsonObject & { $type: CMSContentType };
      return cmsContentPresenter(typedData);
    }
    return null;
  }

  static getKey = () => 'cmsContentForPath';
}
